import { createContext, useContext, useState, useEffect } from "react";
import { 
  auth, 
  db, 
  googleProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
  EmailAuthProvider,
  linkWithCredential
} from "@/firebase";
import { signInWithPopup, signOut, onAuthStateChanged, signInAnonymously, linkWithPopup } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { DEMO_USER } from "@/data/demoUser";
import { getSavedB2BInvite, fetchCompanyName, clearSavedB2BInvite } from "@/services/b2bInviteService";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      if (typeof window !== "undefined" && sessionStorage.getItem("we360.isDemo") === "true") {
        return DEMO_USER;
      }
    } catch (e) {}
    return null;
  });
  const [loading, setLoading] = useState(!user);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Don't override demo user with Firebase state
      if (user?.isDemo || (typeof window !== "undefined" && sessionStorage.getItem("we360.isDemo") === "true")) {
        setLoading(false);
        return;
      }

      if (firebaseUser) {
        // Check if user document exists in Firestore
        const userRef = doc(db, "users", firebaseUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (!userSnap.exists()) {
          // Check for saved B2B invite to link corporate membership
          const savedInvite = getSavedB2BInvite();
          let b2bData = {};
          if (savedInvite?.cid) {
            const companyName = await fetchCompanyName(savedInvite.cid);
            b2bData = {
              clientId: savedInvite.cid,
              empresa: companyName,
              isB2B: true,
              clientRole: "member",
              onboardingStep: "scan_pending",
              linkedVia: "deep_linking",
              linkedAt: new Date().toISOString(),
            };
            clearSavedB2BInvite();
          }

          // Create new user document
          const primaryProvider = firebaseUser.providerData?.[0]?.providerId || (firebaseUser.isAnonymous ? 'anonymous' : 'unknown');
          await setDoc(userRef, {
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            authProvider: primaryProvider,
            createdAt: serverTimestamp(),
            profile: null,
            ...b2bData,
          });
        } else {
          // Update provider if missing (backfill existing users)
          const data = userSnap.data();
          if (!data.authProvider && firebaseUser.providerData?.length > 0) {
            const primaryProvider = firebaseUser.providerData[0].providerId;
            await setDoc(userRef, { authProvider: primaryProvider }, { merge: true });
          }
        }
        
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          isAnonymous: firebaseUser.isAnonymous,
          isDemo: false,
          providerData: firebaseUser.providerData || [],
        });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.isDemo]);

  const clearDemoSession = () => {
    try {
      if (typeof window !== "undefined") {
        sessionStorage.removeItem("we360.isDemo");
      }
    } catch (e) {}
  };

  const loginWithGoogle = async () => {
    try {
      clearDemoSession();
      const result = await signInWithPopup(auth, googleProvider);
      return { success: true, user: result.user };
    } catch (error) {
      console.error("Google login error:", error);
      if (error.code === 'auth/popup-closed-by-user') {
        return { success: false, error: null };
      }
      if (error.code === 'auth/popup-blocked') {
        return { success: false, error: "Tu navegador bloqueó la ventana de inicio de sesión. Permite los popups e intenta de nuevo." };
      }
      return { success: false, error: error.message };
    }
  };

  // Check if email exists in Firebase Auth
  const checkEmailExists = async (email) => {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      if (methods && methods.length > 0) {
        return { exists: true, methods };
      }

      return { exists: false, methods: [] };
    } catch (error) {
      console.warn("Check email error:", error);
      if (error.code === 'auth/invalid-email') {
        return { exists: false, methods: [], error: "El formato del correo no es válido." };
      }
      return { exists: false, methods: [] };
    }
  };

  // Register with email/password
  const registerWithEmail = async (email, password) => {
    try {
      clearDemoSession();
      const result = await createUserWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      console.error("Register error:", error);
      switch (error.code) {
        case 'auth/email-already-in-use':
          return { success: false, error: "Este correo ya está registrado. Por favor, inicia sesión con tu contraseña.", errorCode: 'email-already-in-use' };
        case 'auth/network-request-failed':
          return { success: false, error: "Error de conexión. Verifica tu internet e intenta de nuevo.", errorCode: 'network-request-failed' };
        case 'auth/weak-password':
          return { success: false, error: "La contraseña debe tener al menos 8 caracteres." };
        case 'auth/invalid-email':
          return { success: false, error: "El formato del correo no es válido." };
        default:
          return { success: false, error: "Error al crear la cuenta. Intenta de nuevo." };
      }
    }
  };

  // Login with email/password
  const loginWithEmail = async (email, password) => {
    try {
      clearDemoSession();
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      console.error("Login error:", error);
      switch (error.code) {
        case 'auth/wrong-password':
        case 'auth/user-not-found':
        case 'auth/invalid-credential':
          return { success: false, error: "Email o contraseña incorrectos." };
        case 'auth/too-many-requests':
          return { success: false, error: "Demasiados intentos. Espera unos minutos e intenta de nuevo." };
        case 'auth/invalid-email':
          return { success: false, error: "El formato del correo no es válido." };
        default:
          return { success: false, error: "Error al iniciar sesión. Intenta de nuevo." };
      }
    }
  };

  // Send password reset email
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error("Reset password error:", error);
      switch (error.code) {
        case 'auth/user-not-found':
          // Don't reveal if user exists for security
          return { success: true }; 
        case 'auth/invalid-email':
          return { success: false, error: "El formato del correo no es válido." };
        case 'auth/too-many-requests':
          return { success: false, error: "Demasiados intentos. Espera unos minutos e intenta de nuevo." };
        default:
          return { success: false, error: "Error al enviar el correo. Intenta de nuevo." };
      }
    }
  };

  const loginAsGuest = async () => {
    try {
      clearDemoSession();
      const result = await signInAnonymously(auth);
      return { success: true, user: result.user };
    } catch (error) {
      console.error("Guest login error:", error);
      return { success: false, error: error.message };
    }
  };

  const loginAsDemo = () => {
    try {
      if (typeof window !== "undefined") {
        sessionStorage.setItem("we360.isDemo", "true");
      }
    } catch (e) {}
    setUser(DEMO_USER);
    return { success: true };
  };

  const exitDemo = () => {
    try {
      if (typeof window !== "undefined") {
        sessionStorage.removeItem("we360.isDemo");
      }
    } catch (e) {}
    setUser(null);
  };

  const upgradeGuestToGoogle = async () => {
    try {
      if (!user || !auth.currentUser?.isAnonymous) {
        return { success: false, error: "No hay sesión de invitado activa" };
      }
      const result = await linkWithPopup(auth.currentUser, googleProvider);
      setUser({
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        isAnonymous: false,
        isDemo: false,
        providerData: result.user.providerData || [],
      });
      return { success: true, user: result.user };
    } catch (error) {
      console.error("Upgrade error:", error);
      if (error.code === 'auth/credential-already-in-use') {
        return { success: false, error: "Esta cuenta de Google ya está registrada. Por favor cierra sesión e inicia con Google." };
      }
      if (error.code === 'auth/popup-closed-by-user') {
        return { success: false, error: null };
      }
      return { success: false, error: error.message };
    }
  };

  // Upgrade guest to email/password
  const upgradeGuestToEmail = async (email, password) => {
    try {
      if (!user || !auth.currentUser?.isAnonymous) {
        return { success: false, error: "No hay sesión de invitado activa" };
      }
      const credential = EmailAuthProvider.credential(email, password);
      const result = await linkWithCredential(auth.currentUser, credential);
      setUser({
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        isAnonymous: false,
        isDemo: false,
        providerData: result.user.providerData || [],
      });
      return { success: true, user: result.user };
    } catch (error) {
      console.error("Upgrade to email error:", error);
      if (error.code === 'auth/email-already-in-use') {
        return { success: false, error: "Este correo ya tiene una cuenta. Cierra sesión e inicia con ese correo." };
      }
      if (error.code === 'auth/weak-password') {
        return { success: false, error: "La contraseña debe tener al menos 8 caracteres." };
      }
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      if (typeof window !== "undefined") {
        sessionStorage.removeItem("we360.isDemo");
      }
    } catch (e) {}
    if (user?.isDemo) {
      setUser(null);
      return { success: true };
    }
    
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      console.error("Logout error:", error);
      return { success: false, error: error.message };
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      loginWithGoogle,
      checkEmailExists,
      registerWithEmail,
      loginWithEmail,
      resetPassword,
      loginAsGuest, 
      loginAsDemo,
      exitDemo,
      upgradeGuestToGoogle,
      upgradeGuestToEmail,
      logout 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
